import { Button, IconButton, Switch, TextField, Tooltip } from '@mui/material'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useFormik } from 'formik'
import * as React from 'react'
import { FC } from 'react'
import * as yup from 'yup'
import CircleInformationIcon from '../../assets/icons/CircleInformation.svg'
import { ModalDialogWrapper } from '../../components/ModalDialogWrapper'
import { ProtectedIcon } from '../../components/ProtectedIcon/ProtectedIcon'
import { useProfileStore } from '../../store/storeProfile'
import { TariffType } from '../../swagger/dto'

type Props = {
    open: boolean
    handleClose: VoidFunction
    id: string
}
const validationSchema = yup.object({
    title: yup
        .string()
        .matches(
            /[a-zA-Zа-яА-Я0-9,.;:?!№()-]/,
            'Название проекта должно содержать только буквы (русские и латинские), цифры и спецсимволы: ,.;:?!№()-',
        )
        .required('Название обязательное'),
})
export const ProjectCreateDialog: FC<Props> = ({ open, handleClose, createProject }) => {
    const { userTariff } = useProfileStore((state) => state)
    const isProtectedAvailable =
        userTariff?.tariff?.tariff_type === TariffType.START ||
        userTariff?.tariff?.tariff_type === TariffType.PROFESSIONAL
    const [isProtected, setisProtected] = React.useState(false)

    const handelClickIsProtected = async (event: React.ChangeEvent<HTMLInputElement>) => {
        await setisProtected(event.target.checked)
    }

    const handelClickClose = () => {
        handleClose()
        setisProtected(false)
        resetForm()
    }

    const {
        handleSubmit,
        values,
        handleBlur,
        touched,
        errors,
        handleChange,
        isValid,
        dirty,
        resetForm,
    } = useFormik({
        initialValues: {
            title: null,
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            createProject({ data: { title: values.title, is_protected: isProtected } })
            handelClickClose()
        },
    })

    return (
        <ModalDialogWrapper
            open={open}
            handleClose={handelClickClose}
            colorSubmitButton="primary"
            title="Новый проект">
            <Typography variant="body2" mb={1}></Typography>

            <form onSubmit={handleSubmit}>
                <TextField
                    autoFocus
                    sx={{
                        marginBottom: '16px',
                        marginTop: 0,
                        width: '100%',
                    }}
                    margin="normal"
                    fullWidth
                    id="title"
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                />
                <Typography variant="body2" sx={{ color: '#5A5C5E' }}>
                    Проект — это общее пространство, где можно создавать много разных запросов.
                    Здесь ты настраиваешь инструкции, загружаешь файлы, а ИИ учитывает весь этот
                    контекст во всех запросах внутри проекта
                </Typography>

                <Stack
                    my={2}
                    sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                    direction="row">
                    <Stack direction="row" gap={1} sx={{ alignItems: 'center' }}>
                        <ProtectedIcon />
                        <Typography
                            variant="caption1"
                            component="p"
                            sx={{
                                color: '#181B25',
                            }}>
                            Защищенный проект
                        </Typography>
                        <Tooltip
                            enterTouchDelay={0}
                            title="«Защищённый проект» — все данные хранятся только на серверах ARHITEX в РФ, полностью принадлежащих и контролируемых компанией. При работе используются только защищённые нейросети в агрегаторе нейросетей">
                            <IconButton sx={{ p: 0, width: '20px', height: '20px' }}>
                                <CircleInformationIcon color="#909295" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                    <Switch
                        disabled={!isProtectedAvailable}
                        onChange={handelClickIsProtected}
                        checked={isProtected}
                        size="small"
                    />
                </Stack>

                <Stack direction="row" gap={1} sx={{ padding: ' 8px 0 8px 0' }}>
                    <Button fullWidth variant="outlined" onClick={handleClose}>
                        Отменить
                    </Button>
                    <Button
                        type="submit"
                        disabled={!(isValid && dirty)}
                        fullWidth
                        variant="contained">
                        Создать
                    </Button>
                </Stack>
            </form>
        </ModalDialogWrapper>
    )
}
